import {
	Box,
	Typography,
	TextField,
	Paper,
	Grid,
	InputAdornment,
	CircularProgress,
	MenuItem,
	Button,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import { getUserProfile, updateUserAddress } from '../services/userService';
import { UserProfile } from '../interfaces/user.interface';
import ModalComponent from '../components/modal/modal.component';
import { capitalizeWords } from '../utils/formatter';
import { getProvinces, getMunicipalities } from '../services/addressService';
import { LocationOn } from '@mui/icons-material';

export const EditUserPage = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [userData, setUserData] = useState<UserProfile | null>(null);
	const [address, setAddress] = useState('');
	const [addressInfo, setAddressInfo] = useState('');
	const [cellphone, setCellphone] = useState('');
	const [province, setProvince] = useState('');
	const [city, setCity] = useState('');
	const [provinces, setProvinces] = useState<any[]>([]);
	const [municipalities, setMunicipalities] = useState<any[]>([]);

	const startPosition = { zoom: 4, lat: -38.416097, lng: -63.616672 };
	const [position, setPosition] = useState({
		zoom: startPosition.zoom,
		lat: startPosition.lat,
		lng: startPosition.lng,
		firstMap: true,
		hasMarker: false,
	});

	useEffect(() => {
		const fetchProvinces = async () => {
			try {
				const data = await getProvinces();
				setProvinces(data);
			} catch (error) {
				console.error('Error fetching provinces:', error);
			}
		};
		fetchProvinces();
	}, []);

	const handleMunicipalityChange = async (
		event: React.ChangeEvent<{ value: unknown }>,
	) => {
		const value = event.target.value as string;
		setCity(value);
	};

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				const data = await getUserProfile();
				setUserData(data);
				setAddress(data.address || '');
				setAddressInfo(data.addressInfo || '');
				setCellphone(data.cellphone || '');
				setProvince(data.province || '');
				setCity(data.city || '');

				if (data.latitude && data.longitude) {
					setPosition({
						zoom: 16,
						lat: parseFloat(data.latitude),
						lng: parseFloat(data.longitude),
						firstMap: false,
						hasMarker: true,
					});
				}
			} catch (error) {
				console.error('Error fetching user data:', error);
				navigate('/login');
			} finally {
				setLoading(false);
			}
		};

		fetchUserData();
	}, [navigate]);

	useEffect(() => {
		if (provinces.length > 0 && userData?.province) {
			const selectedProvince = provinces.find(
				(prov: any) => prov.nombre === userData.province,
			);
			if (selectedProvince) {
				getMunicipalities(selectedProvince.id).then(setMunicipalities);
			}
		}
	}, [provinces, userData]);

	const handleProvinceChange = async (
		event: React.ChangeEvent<{ value: unknown }>,
	) => {
		const value = event.target.value as string;
		try {
			const data = await getMunicipalities(value);
			setMunicipalities(data);
		} catch (error) {
			console.error('Error fetching municipalities:', error);
		}
		setProvince(value);
	};

	const [modalOpen, setModalOpen] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);

	const handleSave = async () => {
		if (!userData) return;

		try {
			console.info('Guardando cambios de usuario:', {
				cellphone,
				address,
				city,
				province,
			});
			await updateUserAddress({
				cellphone: cellphone,
				address: address,
				city: city,
				province: province,
			});
			setIsSuccess(true);
			setModalOpen(true);
		} catch (error: any) {
			console.error('Error al guardar los cambios:', error.message);
			setIsSuccess(false);
			setModalOpen(true);
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!userData) {
		return null;
	}

	return (
		<Box
			sx={{
				padding: '20px',
				maxWidth: '800px',
				margin: '0 auto',
				minHeight: '100vh',
			}}
		>
			<Typography variant="h4" sx={{ marginBottom: '30px' }}>
				Editar Datos Personales
			</Typography>

			<Paper
				elevation={3}
				sx={{
					padding: '20px',
					marginBottom: '20px',
				}}
			>
				<Grid container spacing={3}>
					{/* Fields that cannot be edited */}
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Email"
							value={userData.email}
							disabled
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<EmailIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Nombre completo"
							value={`${capitalizeWords(userData.firstName)} ${capitalizeWords(
								userData.lastName,
							)}`}
							disabled
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PersonIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					{/* Editable fields */}
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Celular"
							value={cellphone}
							onChange={(e) => setCellphone(e.target.value)}
							placeholder=" "
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PhoneIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							select
							label="Provincia"
							name="province"
							value={province}
							onChange={handleProvinceChange}
							required
							fullWidth
							margin="normal"
						>
							{provinces.map((province_item) => (
								<MenuItem key={province_item.id} value={province_item.nombre}>
									{province_item.nombre}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							select
							label="Ciudad"
							name="municipality"
							value={city}
							onChange={handleMunicipalityChange}
							required
							fullWidth
							margin="normal"
						>
							{municipalities.map((municipality) => (
								<MenuItem key={municipality.id} value={municipality.nombre}>
									{municipality.nombre}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Dirección"
							value={address}
							placeholder=" "
							onChange={(e) => setAddress(e.target.value)}
							helperText={
								<span className="helper-text">
									Ingresar calle y número, datos catastrales, o ruta y km
								</span>
							}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LocationOn />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				</Grid>
			</Paper>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '10px',
				}}
			>
				<Button
					onClick={() => navigate('/')}
					variant="outlined"
					color="primary"
				>
					Cancelar
				</Button>
				<Button onClick={handleSave} variant="contained" color="primary">
					Guardar
				</Button>
			</Box>
			<ModalComponent
				open={modalOpen}
				title={isSuccess ? '¡Usuario actualizado!' : 'Error'}
				description={
					isSuccess
						? 'Tus datos personales fueron actualizados exitosamente.'
						: 'Ocurrió un error al actualizar los datos.'
				}
				imageSrc="/cambiapp_logo.png"
				buttonText="Volver al inicio"
				onButtonClick={() => navigate('/')}
				onClose={() => navigate('/')}
			/>
		</Box>
	);
};
